import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { money, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ArrowLeft } from "lucide-react";

const emptyLine = () => ({ description: "", quantity: 1, unit_price: 0, gl_account: "" });

export default function PurchaseRequestNew() {
    const navigate = useNavigate();
    const [vendors, setVendors] = useState([]);
    const [gls, setGls] = useState([]);
    const [vendorId, setVendorId] = useState("");
    const [department, setDepartment] = useState("");
    const [needBy, setNeedBy] = useState("");
    const [justification, setJustification] = useState("");
    const [lines, setLines] = useState([emptyLine()]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
        api.get("/gl-accounts").then((r) => setGls(r.data)).catch(() => {});
    }, []);

    const setLine = (i, key, val) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)));
    const addLine = () => setLines((ls) => [...ls, emptyLine()]);
    const delLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));
    const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);

    const build = () => {
        const v = vendors.find((x) => x.id === vendorId);
        return {
            vendor_id: vendorId || null,
            vendor_name: v?.name || null,
            department,
            need_by: needBy || null,
            justification,
            line_items: lines.map((l) => ({
                description: l.description,
                quantity: Number(l.quantity || 0),
                unit_price: Number(l.unit_price || 0),
                gl_account: l.gl_account || null,
            })),
        };
    };

    const valid = () => {
        if (!vendorId) { toast.error("Select a vendor"); return false; }
        if (lines.some((l) => !l.description || !l.gl_account)) { toast.error("Each line needs a description and GL account"); return false; }
        return true;
    };

    const save = async (submit) => {
        if (!valid()) return;
        setBusy(true);
        try {
            const { data: pr } = await api.post("/purchase-requests", build());
            if (submit) {
                await api.post(`/purchase-requests/${pr.id}/submit`);
                toast.success("Request submitted for approval");
            } else {
                toast.success("Draft saved");
            }
            navigate(`/app/requests/${pr.id}`);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 pe-fade-up max-w-4xl">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald" data-testid="back-button">
                <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">New Purchase Request</h1>

            <div className="bg-white border border-border rounded-xl p-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <Label>Vendor</Label>
                        <Select value={vendorId} onValueChange={setVendorId}>
                            <SelectTrigger data-testid="vendor-select" className="mt-1.5"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                            <SelectContent>
                                {vendors.map((v) => (
                                    <SelectItem key={v.id} value={v.id} data-testid={`vendor-option-${v.vx_vendor_id}`}>{v.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="dept">Department / Cost Center</Label>
                        <Input id="dept" value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Technology" className="mt-1.5" data-testid="department-input" />
                    </div>
                    <div>
                        <Label htmlFor="needby">Need by</Label>
                        <Input id="needby" type="date" value={needBy} onChange={(e) => setNeedBy(e.target.value)} className="mt-1.5" data-testid="needby-input" />
                    </div>
                </div>
                <div>
                    <Label htmlFor="just">Justification</Label>
                    <Textarea id="just" value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Why is this purchase needed?" className="mt-1.5" data-testid="justification-input" />
                </div>
            </div>

            {/* Line items */}
            <div className="bg-white border border-border rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <h3 className="font-semibold text-brand-emerald">Line Items</h3>
                    <Button variant="outline" size="sm" onClick={addLine} className="rounded-lg" data-testid="add-line-button"><Plus className="h-4 w-4 mr-1" /> Add line</Button>
                </div>
                <div className="p-4 space-y-3">
                    {lines.map((l, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`line-item-${i}`}>
                            <div className="col-span-12 sm:col-span-4">
                                <Label className="text-xs">Description</Label>
                                <Input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className="mt-1" data-testid={`line-desc-${i}`} />
                            </div>
                            <div className="col-span-4 sm:col-span-3">
                                <Label className="text-xs">GL Account</Label>
                                <Select value={l.gl_account} onValueChange={(v) => setLine(i, "gl_account", v)}>
                                    <SelectTrigger className="mt-1 font-mono text-xs" data-testid={`line-gl-${i}`}><SelectValue placeholder="GL" /></SelectTrigger>
                                    <SelectContent>
                                        {gls.map((g) => (
                                            <SelectItem key={g.gl_account_number} value={g.gl_account_number} className="font-mono text-xs">
                                                {g.gl_account_number} · {g.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-3 sm:col-span-2">
                                <Label className="text-xs">Qty</Label>
                                <Input type="number" min="0" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="mt-1" data-testid={`line-qty-${i}`} />
                            </div>
                            <div className="col-span-3 sm:col-span-2">
                                <Label className="text-xs">Unit Price</Label>
                                <Input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="mt-1" data-testid={`line-price-${i}`} />
                            </div>
                            <div className="col-span-2 sm:col-span-1 flex justify-end">
                                <Button variant="ghost" size="icon" onClick={() => delLine(i)} className="text-rose-600 hover:bg-rose-50" data-testid={`line-delete-${i}`}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-6 bg-secondary/40">
                    <span className="text-sm text-muted-foreground">Total</span>
                    <span className="text-xl font-semibold font-mono text-brand-emerald" data-testid="pr-total">{money(total)}</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-end">
                <Button variant="outline" disabled={busy} onClick={() => save(false)} className="rounded-lg" data-testid="save-draft-button">Save draft</Button>
                <Button disabled={busy} onClick={() => save(true)} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="submit-request-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Submit for Approval"}
                </Button>
            </div>
        </div>
    );
}
